import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import { useToast } from '@/hooks/use-toast';
import { useAuthContext } from '@/contexts/AuthContext';

type Task = Tables<'tasks'>;
type TaskInsert = TablesInsert<'tasks'>;
type TaskUpdate = TablesUpdate<'tasks'>;

export interface TaskAssignee {
  user_id: string;
  profile?: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
    profile_color: string | null;
  } | null;
}

export interface TaskWithProject extends Task {
  projects?: { name: string; product_type?: string } | null;
  task_assignees?: TaskAssignee[];
  notes_count?: number;
}

export const fetchTasksFromDb = async (projectId?: string): Promise<TaskWithProject[]> => {
  let query = supabase
    .from('tasks')
    .select('*, projects(name, product_type), task_assignees(user_id)')
    .order('updated_at', { ascending: false })
    .limit(500);

  if (projectId) {
    query = query.eq('project_id', projectId);
  }

  const { data, error } = await query;
  if (error) throw error;

  // Fetch assignee profiles
  const allUserIds = new Set<string>();
  (data || []).forEach(task => {
    if (task.assigned_to) allUserIds.add(task.assigned_to);
    (task.task_assignees || []).forEach((a: any) => {
      if (a.user_id) allUserIds.add(a.user_id);
    });
  });

  let profilesMap: Record<string, { id: string; full_name: string | null; avatar_url: string | null; profile_color: string | null }> = {};
  if (allUserIds.size > 0) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name, avatar_url, profile_color')
      .in('id', Array.from(allUserIds));

    profilesMap = (profiles || []).reduce((acc, p) => {
      acc[p.id] = p;
      return acc;
    }, {} as typeof profilesMap);
  }

  // Fetch notes count
  const taskIds = (data || []).map(task => task.id);
  let notesCountMap: Record<string, number> = {};
  if (taskIds.length > 0) {
    const { data: notesCounts } = await supabase
      .from('task_notes')
      .select('task_id')
      .in('task_id', taskIds);

    (notesCounts || []).forEach((note: { task_id: string }) => {
      notesCountMap[note.task_id] = (notesCountMap[note.task_id] || 0) + 1;
    });
  }

  return (data || []).map(task => ({
    ...task,
    task_assignees: (task.task_assignees || []).map((a: any) => ({
      user_id: a.user_id,
      profile: profilesMap[a.user_id] || null,
    })),
    notes_count: notesCountMap[task.id] || 0,
  }));
};

export const useTasks = (projectId?: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuthContext();

  const queryKey = projectId ? ['tasks', projectId] : ['tasks'];

  const { data: tasks = [], isLoading: loading, refetch } = useQuery({
    queryKey,
    queryFn: () => fetchTasksFromDb(projectId),
    enabled: !!user && !authLoading,
  });

  const createTask = async (task: Omit<TaskInsert, 'created_by'>, assigneeIds?: string[]) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('tasks')
        .insert({ ...task, created_by: user?.id })
        .select('*, projects(name)')
        .single();

      if (error) throw error;

      if (assigneeIds && assigneeIds.length > 0) {
        const { error: assigneeError } = await supabase
          .from('task_assignees')
          .insert(assigneeIds.map(userId => ({ task_id: data.id, user_id: userId })));
        if (assigneeError) console.error('Error adding assignees:', assigneeError);
      }

      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast({ title: 'Aufgabe erstellt', description: 'Die Aufgabe wurde erfolgreich erstellt.' });
      return data;
    } catch (error: any) {
      console.error('Error creating task:', error);
      toast({ variant: 'destructive', title: 'Fehler', description: 'Aufgabe konnte nicht erstellt werden.' });
      throw error;
    }
  };

  const updateTask = async (id: string, updates: TaskUpdate, assigneeIds?: string[]) => {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .update(updates)
        .eq('id', id)
        .select('*, projects(name)')
        .single();

      if (error) throw error;

      if (assigneeIds !== undefined) {
        await supabase.from('task_assignees').delete().eq('task_id', id);
        if (assigneeIds.length > 0) {
          const { error: assigneeError } = await supabase
            .from('task_assignees')
            .insert(assigneeIds.map(userId => ({ task_id: id, user_id: userId })));
          if (assigneeError) console.error('Error updating assignees:', assigneeError);
        }
      }

      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast({ title: 'Aufgabe aktualisiert', description: 'Die Änderungen wurden gespeichert.' });
      return data;
    } catch (error: any) {
      console.error('Error updating task:', error);
      toast({ variant: 'destructive', title: 'Fehler', description: 'Aufgabe konnte nicht aktualisiert werden.' });
      throw error;
    }
  };

  const deleteTask = async (id: string) => {
    try {
      const { error } = await supabase.from('tasks').delete().eq('id', id);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast({ title: 'Aufgabe gelöscht', description: 'Die Aufgabe wurde erfolgreich gelöscht.' });
    } catch (error: any) {
      console.error('Error deleting task:', error);
      toast({ variant: 'destructive', title: 'Fehler', description: 'Aufgabe konnte nicht gelöscht werden.' });
      throw error;
    }
  };

  return {
    tasks,
    loading,
    fetchTasks: refetch,
    createTask,
    updateTask,
    deleteTask,
  };
};
